'use client';
import { Combination, OptionType, OptionValue, Variant } from '@castlery/modules-product-domain';
import { useCallback, useMemo } from 'react';
import { displayMode } from '../product-configurable-options/product-options-values';
import { FortressImage, OptionSelector } from '@castlery/shared-components';
import { Button, RadioButton, RadioGroup, Stack, useBreakpoints } from '@castlery/fortress';
import { ProductFabricTooltip } from '../product-configurable-options/product-fabrc-tooltip';

interface ProductBundleValuesProps {
  optionType?: OptionType;
  variant?: Variant;
  bundleVariants?: Variant[];
  combinations?: Combination[];
  onOptionClick?: (tempBundleVariant: Variant) => void;
}

export const ProductBundleValues = (props: ProductBundleValuesProps) => {
  const { optionType, variant, combinations, bundleVariants, onOptionClick } = props;
  const currentDisplayMode = displayMode[optionType?.name as keyof typeof displayMode] || 'rectangle';
  const { desktop } = useBreakpoints();
  const clickHandler = useCallback(
    (targetCombinationId?: number, optionValue?: OptionValue) => {
      if (!targetCombinationId) {
        const targetCombination = combinations?.find((combination) => {
          if (optionType) {
            return combination[optionType?.name] === optionValue?.name;
          }
          return false;
        });
        targetCombinationId = targetCombination?.id;
        if (!targetCombinationId) return;
      }
      const targetVariant = bundleVariants?.find((variant) => variant.id === targetCombinationId);
      if (!targetVariant) return;
      onOptionClick?.({
        ...variant,
        ...targetVariant,
      });
    },
    [bundleVariants, combinations, onOptionClick, optionType, variant]
  );

  const optionValues = useMemo(() => {
    const optionOrderMap = new Map();
    if (combinations && optionType?.name) {
      combinations.forEach((combination, index) => {
        const optionValueName = combination[optionType.name];
        if (optionValueName && !optionOrderMap.has(optionValueName)) {
          optionOrderMap.set(optionValueName, index);
        }
      });
    }

    let values = optionType?.values
      ?.filter(({ name }) => combinations?.find((combination) => combination[optionType.name] === name))
      ?.map((optionValue) => {
        const optionMap = new Map(
          variant?.variant_option_values?.map((option) => [option.option_type_name, option.name])
        );
        const isSelected = optionMap.get(optionType.name) === optionValue.name;
        optionMap.set(optionType.name, optionValue.name);
        const currentOptionCombination = Array.from(optionMap.entries());
        const targetCombination = combinations?.find((combination) => {
          return currentOptionCombination.every(
            ([key, value]) =>
              combination[key as keyof typeof combination]?.toString() === value && combination.availableForSale
          );
        });
        return {
          id: optionValue?.id?.toString(),
          name: optionValue?.name,
          value: optionValue?.presentation,
          label: optionValue?.presentation,
          image: optionValue?.image_url,
          isSelected,
          onSelect: () => clickHandler(targetCombination?.id, optionValue),
        };
      })
      ?.sort((a, b) => {
        const aOrder = optionOrderMap.get(a.name) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = optionOrderMap.get(b.name) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });

    // Sort orientation values with 'left' items first
    if (optionType?.name === 'orientation' && values) {
      values = values.sort((a, b) => {
        const aHasLeft = a.value?.toLowerCase().includes('left');
        const bHasLeft = b.value?.toLowerCase().includes('left');
        if (aHasLeft && !bHasLeft) return -1;
        if (!aHasLeft && bHasLeft) return 1;
        return 0;
      });
    }

    return values;
  }, [optionType?.values, optionType?.name, variant?.variant_option_values, combinations, clickHandler]);

  const handleSelect = useCallback(
    (optionId: string) => {
      const selectedOption = optionValues?.find((option) => option.id === optionId);
      if (selectedOption) {
        selectedOption.onSelect();
      }
    },
    [optionValues]
  );

  return (
    <>
      {currentDisplayMode === 'circle' ? (
        <OptionSelector
          options={optionValues || []}
          onSelect={handleSelect}
          maxDisplay={20}
          showAdditionalCount={false}
          sx={{
            flexWrap: 'wrap',
          }}
          isProductPage={true}
          isNeedTooltip={desktop ? true : false}
          TooltipContent={ProductFabricTooltip}
        />
      ) : (
        <RadioGroup
          name="bundle-options"
          value={optionValues?.find((option) => option.isSelected)?.id || ''}
          onChange={(event) => {
            const selectedOption = optionValues?.find((option) => option.id === event.target.value);
            selectedOption?.onSelect();
          }}
        >
          <Stack direction={'row'} flexWrap={'wrap'} gap={3}>
            {optionValues?.map((option) => (
              <RadioButton
                key={option.id}
                label={
                  option.image ? (
                    <FortressImage
                      src={option.image}
                      alt={option.label}
                      sx={{ width: 101, height: 40, '--AspectRatio-paddingBottom': 0 }}
                      sizes={101 + 'px'}
                    />
                  ) : (
                    option.label
                  )
                }
                value={option.id}
                sx={{
                  ...(option.image && {
                    px: 2,
                    py: 1,
                  }),
                }}
              />
            ))}
          </Stack>
        </RadioGroup>
      )}
    </>
  );
};
